import React, { useEffect, useMemo, useState } from "react";
import {
  clearAdminAccessoryPdfFile,
  createAdminAccessoryPdf,
  deleteAdminAccessoryPdf,
  updateAdminAccessoryPdf,
  uploadAdminAccessoryPdfFile,
} from "../../../api/metalsAdmin";
import {
  clearAdminMetalFramePdfFile,
  createAdminMetalFramePdf,
  deleteAdminMetalFramePdf,
  updateAdminMetalFramePdf,
  uploadAdminMetalFramePdfFile,
} from "../../../api/metalFramesAdmin";
import {
  clearAdminBoardPdfFile,
  createAdminBoardPdf,
  deleteAdminBoardPdf,
  updateAdminBoardPdf,
  uploadAdminBoardPdfFile,
} from "../../../api/boardsAdmin";
import {
  clearAdminSolutionPdfFile,
  createAdminSolutionPdf,
  deleteAdminSolutionPdf,
  updateAdminSolutionPdf,
  uploadAdminSolutionPdfFile,
} from "../../../api/solutionsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";

const sortDocs = (list) =>
  [...(Array.isArray(list) ? list : [])].sort(
    (a, b) => Number(a?.displayOrder ?? 0) - Number(b?.displayOrder ?? 0) || Number(a?.id ?? 0) - Number(b?.id ?? 0)
  );

/**
 * @param {{ variant: 'accessory' | 'metalFrame' | 'board' | 'solution', productId: number, productSlug?: string, pdfDocuments: unknown[], onRefresh: () => Promise<void> }} props
 */
const AdminProductPdfsPanel = ({ variant, productId, productSlug = "", pdfDocuments, onRefresh }) => {
  const { notify } = useNotification();
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState({});
  const [rows, setRows] = useState([]);

  const sortedSource = useMemo(() => sortDocs(pdfDocuments), [pdfDocuments]);

  useEffect(() => {
    setRows(
      sortedSource.map((d) => ({
        id: d.id,
        title: d.title ?? "",
        displayOrder: Number(d.displayOrder ?? 0),
        isActive: Boolean(d.isActive),
        fileUrl: d.fileUrl ?? null,
        createdAtUtc: d.createdAtUtc ?? "",
      }))
    );
  }, [sortedSource, productId]);

  const api = useMemo(() => {
    if (variant === "metalFrame") {
      return {
        create: createAdminMetalFramePdf,
        update: updateAdminMetalFramePdf,
        upload: uploadAdminMetalFramePdfFile,
        remove: deleteAdminMetalFramePdf,
        clearFile: clearAdminMetalFramePdfFile,
      };
    }
    if (variant === "board") {
      return {
        create: createAdminBoardPdf,
        update: updateAdminBoardPdf,
        upload: uploadAdminBoardPdfFile,
        remove: deleteAdminBoardPdf,
        clearFile: clearAdminBoardPdfFile,
      };
    }
    if (variant === "solution") {
      return {
        create: createAdminSolutionPdf,
        update: updateAdminSolutionPdf,
        upload: uploadAdminSolutionPdfFile,
        remove: deleteAdminSolutionPdf,
        clearFile: clearAdminSolutionPdfFile,
      };
    }
    return {
      create: createAdminAccessoryPdf,
      update: updateAdminAccessoryPdf,
      upload: uploadAdminAccessoryPdfFile,
      remove: deleteAdminAccessoryPdf,
      clearFile: clearAdminAccessoryPdfFile,
    };
  }, [variant]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleAddSlot = () => {
    const title = newTitle.trim();
    if (!title) {
      notify({ type: "error", title: "Enter a title for the new PDF row" });
      return;
    }
    run(async () => {
      try {
        await api.create({ id: productId, title, productSlug });
        setNewTitle("");
        notify({ type: "success", title: "PDF row added" });
        await onRefresh();
      } catch (error) {
        notify({ type: "error", title: "Could not add PDF row", message: error?.response?.data?.message || error?.message || "" });
      }
    });
  };

  const updateRow = (pdfId, patch) => {
    setRows((prev) => prev.map((r) => (r.id === pdfId ? { ...r, ...patch } : r)));
  };

  const handleSaveMeta = (pdfId) => {
    const row = rows.find((r) => r.id === pdfId);
    if (!row) return;
    run(async () => {
      try {
        await api.update({
          id: productId,
          pdfId,
          payload: {
            title: row.title.trim(),
            displayOrder: Number(row.displayOrder) || 0,
            isActive: Boolean(row.isActive),
          },
        });
        notify({ type: "success", title: "PDF updated" });
        await onRefresh();
      } catch (error) {
        notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
      }
    });
  };

  const handleUpload = (pdfId) => {
    const file = pendingFiles[pdfId];
    if (!file) {
      notify({ type: "error", title: "Choose a PDF file first" });
      return;
    }
    run(async () => {
      try {
        await api.upload({ id: productId, pdfId, file });
        setPendingFiles((p) => {
          const next = { ...p };
          delete next[pdfId];
          return next;
        });
        notify({ type: "success", title: "PDF file uploaded" });
        await onRefresh();
      } catch (error) {
        notify({ type: "error", title: "Upload failed", message: error?.response?.data?.message || error?.message || "" });
      }
    });
  };

  const handleClearFile = (pdfId) => {
    if (!window.confirm("Remove the file from this row? The row will stay; you can upload again later.")) return;
    run(async () => {
      try {
        await api.clearFile({ id: productId, pdfId });
        notify({ type: "success", title: "File cleared" });
        await onRefresh();
      } catch (error) {
        notify({ type: "error", title: "Clear failed", message: error?.response?.data?.message || error?.message || "" });
      }
    });
  };

  const handleDeleteRow = (pdfId) => {
    if (!window.confirm("Delete this PDF row permanently?")) return;
    run(async () => {
      try {
        await api.remove({ id: productId, pdfId });
        notify({ type: "success", title: "PDF row deleted" });
        await onRefresh();
      } catch (error) {
        notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
      }
    });
  };

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
      <p className="text-sm font-semibold text-[#011337] mb-1">Product PDFs</p>
      <p className="text-xs text-slate-500 mb-3">
        Add a row with a title, then upload a .pdf. New rows send <code className="text-[11px]">productSlug</code>
        {productSlug ? (
          <>
            {" "}
            (<span className="font-medium text-slate-600">{productSlug}</span>)
          </>
        ) : (
          " (set accessory/frame key or board type on the product)"
        )}
        . Only active rows with a file appear on the public site.
      </p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <label className="text-sm text-slate-700 flex-1 min-w-[180px]">
          New row title
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="e.g. Technical datasheet"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={handleAddSlot}
          className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50 disabled:opacity-60"
        >
          Add PDF row
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No PDF rows yet.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const href = resolveMediaUrl(row.fileUrl);
            return (
              <div key={row.id} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                <div className="flex flex-wrap gap-2 items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">#{row.id}</span>
                  {row.createdAtUtc ? (
                    <span className="text-[11px] text-slate-400">{String(row.createdAtUtc).slice(0, 19).replace("T", " ")}</span>
                  ) : null}
                </div>
                <div className="grid sm:grid-cols-12 gap-2">
                  <label className="sm:col-span-5 text-xs text-slate-700">
                    Title
                    <input
                      type="text"
                      value={row.title}
                      onChange={(e) => updateRow(row.id, { title: e.target.value })}
                      className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="sm:col-span-2 text-xs text-slate-700">
                    Order
                    <input
                      type="number"
                      value={row.displayOrder}
                      onChange={(e) => updateRow(row.id, { displayOrder: e.target.value })}
                      className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="sm:col-span-2 flex items-end gap-2 text-xs text-slate-700 pb-1">
                    <input
                      type="checkbox"
                      checked={row.isActive}
                      onChange={(e) => updateRow(row.id, { isActive: e.target.checked })}
                    />
                    Active
                  </label>
                  <div className="sm:col-span-3 flex flex-wrap gap-2 items-end">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleSaveMeta(row.id)}
                      className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10 disabled:opacity-60"
                    >
                      Save metadata
                    </button>
                  </div>
                </div>
                <div className="text-xs text-slate-600">
                  File:{" "}
                  {row.fileUrl ? (
                    <>
                      <span className="break-all">{row.fileUrl}</span>
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 underline">
                          Open
                        </a>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-amber-700">No file — upload a PDF below.</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    disabled={busy}
                    onChange={(e) =>
                      setPendingFiles((p) => ({
                        ...p,
                        [row.id]: e.target.files?.[0] || null,
                      }))
                    }
                    className="text-xs max-w-full"
                  />
                  <button
                    type="button"
                    disabled={busy || !pendingFiles[row.id]}
                    onClick={() => handleUpload(row.id)}
                    className="text-xs px-2 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                  >
                    Upload PDF
                  </button>
                  <button
                    type="button"
                    disabled={busy || !row.fileUrl}
                    onClick={() => handleClearFile(row.id)}
                    className="text-xs px-2 py-1.5 rounded border border-amber-200 text-amber-900 hover:bg-amber-50 disabled:opacity-60"
                  >
                    Clear file
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleDeleteRow(row.id)}
                    className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    Delete row
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminProductPdfsPanel;
